import React, { useEffect, useState } from 'react';
import { Upload, X, Download, Trash2, FileText, File, Eye, Search } from 'lucide-react';
import '../styles/assetDocumentsSection.css';
import Apis from '../services/apis';

const AssetDocumentsSection = ({ assetId,docs }) => {
  // Sample documents data from state
  
  const [documents, setDocuments] = useState([
    // {
    //   id: '1',
    //   name: 'Certificate of Authenticity',
    //   filename: 'certificate.pdf',
    //   type: 'PDF',
    //   category: 'Certificate',
    //   size: '1.2 MB',
    //   uploadedDate: '2025-01-10',
    //   url: '#'
    // }
  ]);

  useEffect(() => {
    setDocuments(docs);
  },[])
  const apis = new Apis();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // const categories = ['All', 'Certificate', 'Invoice', 'Warranty', 'Service', 'Appraisal', 'Insurance', 'Other'];

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setIsUploading(true);
  
    try {
      for (const file of files) {
        const uniqueFileName = `${Date.now()}-${file.name}`;
  
        // 1️⃣ Get presigned URL from your API
        const presignedRes = await fetch(
          `https://schedulerapi.pricingculture.com/holdings/generate-presigned-url?fileName=${uniqueFileName}&fileType=${file.type}`
        );
  
        if (!presignedRes.ok) throw new Error("Failed to get presigned URL");
  
        const { uploadUrl } = await presignedRes.json();
  
        // 2️⃣ Upload file to S3
        await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
  
        // 3️⃣ Save document record in your DB via backend
        const backendRes = await apis.uploadDocument(assetId, {
          name: file.name.replace(/\.[^/.]+$/, ""),
          fileType: file.name.split(".").pop().toUpperCase(),
          // size: (file.size / (1024 * 1024)).toFixed(1) + " MB",
          sizeKb: Math.ceil(file.size / 1024),
          fileUrl: `https://pc-holding-document-bucket.s3.us-east-2.amazonaws.com/${uniqueFileName}`,
        });

  
        const savedDoc = backendRes.data;
  
        // 4️⃣ Update UI
        setDocuments((prev) => [
          ...prev,
          {
            id: savedDoc.id,
            name: savedDoc.name,
            filename: savedDoc.filename,
            type: savedDoc.type,
            category: savedDoc.category,
            size: savedDoc.size,
            uploadedDate: new Date().toISOString().split("T")[0],
            url: savedDoc.url,
          },
        ]);
      }
    } catch (err) {
      console.error("Document upload failed", err);
    } finally {
      setIsUploading(false);
    }
  };
  

  const handleDelete = (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
    }
  };

  const handleDownload = (doc) => {
    console.log('Downloading:', doc.filename);
  };

  const handleView = (doc) => {
    console.log('Viewing:', doc.filename);
  };

  const getCategoryColor = (category) => {
    const colors = {
      Certificate: { bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981' },
      Invoice: { bg: 'rgba(59, 130, 246, 0.1)', text: '#3b82f6' },
      Warranty: { bg: 'rgba(168, 85, 247, 0.1)', text: '#a855f7' },
      Service: { bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b' },
      Appraisal: { bg: 'rgba(6, 182, 212, 0.1)', text: '#06b6d4' },
      Insurance: { bg: 'rgba(236, 72, 153, 0.1)', text: '#ec4899' },
      Other: { bg: 'rgba(113, 113, 122, 0.1)', text: '#71717a' }
    };
    return colors[category] || colors.Other;
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="documents-section">
      {/* Upload Area */}
      <div className="documents-card">
        <h3 className="documents-card-title">Upload Documents</h3>
        
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`doc-upload-area ${dragActive ? 'doc-upload-area-active' : ''}`}
        >
          <input
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileInput}
            className="doc-upload-input"
            disabled={isUploading}
          />
          
          <div className="doc-upload-content">
            {isUploading ? (
              <div className="doc-upload-loading">
                <div className="doc-upload-spinner"></div>
                <p className="doc-upload-loading-text">Uploading documents...</p>
              </div>
            ) : (
              <div className="doc-upload-instructions">
                <div className="doc-upload-icon-wrapper">
                  <Upload size={32} className="doc-upload-icon" />
                </div>
                <p className="doc-upload-primary-text">
                  Drop documents here or click to browse
                </p>
                <p className="doc-upload-secondary-text">
                  Supports: PDF, DOC, DOCX, JPG, PNG up to 25MB each
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents List */}
      <div className="documents-card">
        <div className="documents-card-header">
          <h3 className="documents-card-title">
            Documents ({filteredDocuments.length})
          </h3>
        </div>

        {/* Filters */}
        <div className="documents-filters">
          {/* Search */}
          <div className="search-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Category Filter */}
          {/* <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select> */}
        </div>

        {/* Documents Table */}
        {filteredDocuments.length === 0 ? (
          <div className="doc-empty-state">
            <div className="doc-empty-state-icon">
              <File size={32} />
            </div>
            <p className="doc-empty-state-text">
              {searchTerm || selectedCategory !== 'All' 
                ? 'No documents match your filters' 
                : 'No documents uploaded yet'}
            </p>
          </div>
        ) : (
          <div className="documents-list">
            {filteredDocuments.map((doc) => {
              const categoryColors = getCategoryColor(doc.category);
              return (
                <div key={doc.id} className="document-item">
                  <div className="document-content">
                    {/* Icon */}
                    <div className="document-icon-wrapper">
                      <FileText size={20} className="document-icon" />
                    </div>

                    {/* Document Info */}
                    <div className="document-info">
                      <div className="document-header">
                        <h4 className="document-name">{doc.name}.{doc.fileType.toLowerCase()}</h4>
                        {/* <span
                          className="document-category-badge"
                          style={{ 
                            backgroundColor: categoryColors.bg,
                            color: categoryColors.text
                          }}
                        >
                          {doc.category}
                        </span> */}
                      </div>
                     
                      <div className="document-meta">
                        <span>{doc.fileType}</span>
                        <span>·</span>
                        <span>{doc.sizeKb}</span>
                        <span>·</span>
                        <span>{doc.createdAt}</span>
                      </div>
                      <div className="document-meta" style={{}}>
                        <span>{doc.fileUrl}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="document-actions">
                    {/* <button
                      onClick={() => handleView(doc)}
                      className="doc-action-button"
                      title="View"
                    >
                      <Eye size={18} />
                    </button> */}
                    {/* <button
                      onClick={() => handleDownload(doc)}
                      className="doc-action-button"
                      title="Download"
                    >
                      <Download size={18} />
                    </button> */}
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="doc-action-button doc-action-button-delete"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDocumentsSection;